<template>
  <div>
    <div class="mt-10 sm:mt-0">
      <div class="md:grid md:grid-cols-3 md:gap-6">
        <div class="md:col-span-1">
          <div class="px-4 sm:px-0">
            <h3 class="text-lg font-medium leading-6 text-gray-900">
              {{ $t('patient_info_title') }}
            </h3>
            <p class="mt-1 text-sm leading-5 text-gray-600">
              {{ $t('patient_info_description') }}
            </p>
          </div>
        </div>
        <div class="mt-5 md:mt-0 md:col-span-2">
          <form action="#" method="POST">
            <div class="shadow overflow-hidden sm:rounded-md">
              <div class="px-4 py-5 bg-white sm:p-6">
                <div class="grid grid-cols-6 gap-6">
                  <div class="col-span-6 sm:col-span-3">
                    <sqr-input-text
                      :label="$t('patient_given_name')"
                      :value="patient.givenName"
                      @change="change('givenName', $event)"
                      :v="$v.givenName"
                    />
                  </div>

                  <div class="col-span-6 sm:col-span-3">
                    <sqr-input-text
                      :label="$t('patient_family_name')"
                      :value="patient.familyName"
                      @change="change('familyName', $event)"
                      :v="$v.familyName"
                    />
                  </div>

                  <div class="col-span-6 sm:col-span-3">
                    <sqr-input-date-num
                      :label="$t('patient_birth_date')"
                      :value="patient.birthDate"
                      @change="change('birthDate', $event)"
                      :v="$v.birthDate"
                    />
                  </div>

                  <div class="col-span-6 sm:col-span-3">
                    <sqr-input-radio
                      :label="$t('patient_gender')"
                      :value="patient.gender"
                      :options="genders"
                      @change="change('gender', $event)"
                      :v="$v.gender"
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

    <div class="hidden sm:block">
      <div class="py-5">
        <div class="border-t border-gray-200"></div>
      </div>
    </div>

    <div class="mt-10 sm:mt-0">
      <div class="md:grid md:grid-cols-3 md:gap-6">
        <div class="md:col-span-1">
          <div class="px-4 sm:px-0">
            <h3 class="text-lg font-medium leading-6 text-gray-900">
              {{ $t('patient_coordinates_title') }}
            </h3>
            <p class="mt-1 text-sm leading-5 text-gray-600">
              {{ $t('patient_coordinates_desc') }}
            </p>
          </div>
        </div>
        <div class="mt-5 md:mt-0 md:col-span-2">
          <form action="#" method="POST">
            <div class="shadow overflow-hidden sm:rounded-md">
              <div class="px-4 py-5 bg-white sm:p-6">
                <div class="grid grid-cols-6 gap-6 mb-6">
                  <sqr-input-text
                    class="col-span-6"
                    :label="$t('address_street')"
                    :value="patient.addressStreet"
                    @change="change('addressStreet', $event)"
                    :v="$v.addressStreet"
                  />

                  <sqr-input-text
                    class="col-span-6 sm:col-span-3 lg:col-span-2"
                    :label="$t('address_postal_code')"
                    :value="patient.postalCode"
                    @change="change('postalCode', $event)"
                    :v="$v.postalCode"
                  />

                  <div class="col-span-6 sm:col-span-6 lg:col-span-2">
                    <sqr-input-text
                      :label="$t('address_locality')"
                      :value="patient.addressLocality"
                      @change="change('addressLocality', $event)"
                      :v="$v.addressLocality"
                    />
                  </div>

                  <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                    <sqr-input-select
                      :label="$t('patient_canton')"
                      :value="patient.canton"
                      @change="change('canton', $event)"
                      :options="cantons"
                      :v="$v.canton"
                    />
                  </div>

                  <div class="col-span-4">
                    <sqr-input-email
                      :label="$t('patient_email')"
                      :value="patient.email"
                      @change="change('email', $event)"
                    />
                  </div>
                  <div class="col-span-3">
                    <sqr-input-text
                      :label="$t('patient_telephone')"
                      :value="patient.telephone"
                      @change="change('telephone', $event)"
                    />
                  </div>

                  <div class="col-span-4">
                    <sqr-input-checkbox
                      :placeholder="$t('patient_address_custom_placeholder')"
                      :value="patient.addressCustom"
                      @change="change('addressCustom', $event)"
                    />
                    <div class="text-xs text-gray-600">
                      {{ $t('patient_address_custom_content_help') }}
                    </div>
                  </div>
                  <div class="col-span-4" v-if="patient.addressCustom">
                    <sqr-input-textarea
                      placeholder="patient_address_custom_content_placeholder"
                      :value="patient.addressCustomContent"
                      @change="change('addressCustomContent', $event)"
                      :rows="4"
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

    <div class="hidden sm:block">
      <div class="py-5">
        <div class="border-t border-gray-200"></div>
      </div>
    </div>

    <div>
      <div class="md:grid md:grid-cols-3 md:gap-6">
        <div class="md:col-span-1">
          <div class="px-4 sm:px-0">
            <h3 class="text-lg font-medium leading-6 text-gray-900">
              {{ $t('patient_info_insurance_title') }}
            </h3>
            <p class="mt-1 text-sm leading-5 text-gray-600">
              {{ $t('patient_info_insurance_desc') }}
            </p>
          </div>
        </div>
        <div class="mt-5 md:mt-0 md:col-span-2">
          <form action="#" method="POST">
            <div class="shadow sm:rounded-md sm:overflow-hidden">
              <div class="px-4 py-5 bg-white sm:p-6">
                <div class="grid grid-cols-3 gap-6">
                  <div class="col-span-6">
                    <sqr-input-select
                      :label="$t('patient_law')"
                      :value="patient.law"
                      :options="laws"
                      @change="change('law', $event)"
                      :v="$v.law"
                    />
                    <div class="help is-danger" v-if="$v.law.$error">
                      {{ $t('validation_required') }}
                    </div>
                  </div>
                  <div class="col-span-6">
                    <sqr-input-radio
                      :label="$t('patient_refund_type')"
                      :value="patient.refundType"
                      :options="refundTypes"
                      @change="change('refundType', $event)"
                      :v="$v.refundType"
                    />
                    <div class="help is-danger" v-if="$v.refundType.$error">
                      {{ $t('validation_required') }}
                    </div>
                  </div>
                  <div class="col-span-6">
                    <sqr-input-checkbox
                      :placeholder="$t('patient_details_placeholder')"
                      :value="patient.details"
                      @change="change('details', $event)"
                    />
                  </div>

                  <div
                    v-show="patient.details"
                    class="col-span-6 grid grid-cols-6 gap-6"
                  >
                    <div class="col-span-3">
                      <sqr-input-text
                        :label="$t('patient_avs_number')"
                        :value="patient.avsNumber"
                        @change="change('avsNumber', $event)"
                      />
                    </div>

                    <sqr-input-text
                      class="col-span-3"
                      :label="$t('patient_cada_number')"
                      :value="patient.cadaNumber"
                      @change="change('cadaNumber', $event)"
                    />

                    <sqr-input-text
                      class="col-span-3"
                      :label="$t('patient_insured_number')"
                      :value="patient.insuredNumber"
                      @change="change('insuredNumber', $event)"
                    />

                    <sqr-input-date
                      class="col-start-1 col-span-3"
                      :label="$t('patient_case_date')"
                      :value="patient.caseDate"
                      @change="change('caseDate', $event)"
                    />

                    <sqr-input-text
                      class="col-span-3"
                      :label="$t('patient_case_number')"
                      :value="patient.caseNumber"
                      @change="change('caseNumber', $event)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
    <!--
x     contractNumber

      role
      companyNumber
      companyName

      therapy
      comment:
    -->
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import SqrInputText from '@/components/SqrInputText';
import SqrInputDate from '@/components/SqrInputDate';
import SqrInputDateNum from '@/components/SqrInputDateNum';
import SqrInputRadio from '@/components/SqrInputRadio';
import SqrInputEmail from '@/components/SqrInputEmail';
import SqrInputSelect from '@/components/SqrInputSelect';
import SqrInputCheckbox from '@/components/SqrInputCheckbox';
import SqrInputTextarea from '@/components/SqrInputTextarea';

export default {
  name: 'PatientForm',
  components: {
    SqrInputText,
    SqrInputDate,
    SqrInputDateNum,
    SqrInputRadio,
    SqrInputEmail,
    SqrInputSelect,
    SqrInputCheckbox,
    SqrInputTextarea
  },
  props: {
    patient: { type: Object, default: () => ({}) },
    validations: { type: Object, default: () => ({}) }
  },
  computed: {
    $v() {
      return this.validations;
    },
    ...mapGetters('options', ['laws', 'refundTypes', 'genders', 'cantons'])
  },
  methods: {
    change(field, value) {
      this.$emit('change', { field, value });
    }
  }
};
</script>
